import React from 'react';
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Stack,
  Button,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import BusinessIcon from '@mui/icons-material/Business';
import ExtensionIcon from '@mui/icons-material/Extension';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useNavigate} from 'react-router-dom';
import {useOrg} from '../contexts/OrgContext';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

export default function SettingsPage() {
  useDocumentTitle('Settings');

  const {activeOrg, organizations, loading: orgLoading, switchOrg} = useOrg();
  const navigate = useNavigate();

  if (orgLoading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
        <CircularProgress />
      </Box>
    );
  }

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const sortedOrgs = organizations;

  const personalSections: {
    label: string;
    description: string;
    path: string;
    icon: typeof ExtensionIcon;
  }[] = [
    {
      label: 'Connections',
      description: 'Personal integrations (Google, Gmail, Drive, …) and run-scoped credentials.',
      path: '/app/settings/connections',
      icon: ExtensionIcon,
    },
    {
      label: 'Notifications',
      description: 'Email + in-app notification preferences.',
      path: '/app/settings/notifications',
      icon: NotificationsIcon,
    },
  ];

  return (
    <Box sx={{flex: 1, bgcolor: 'background.default'}}>
      <Container maxWidth="sm" sx={{py: 4}}>
        <Typography variant="h4" component="h1" sx={{fontWeight: 500, mb: 3}}>
          Settings
        </Typography>

        {/* Personal Section */}
        <Typography variant="h6" sx={{fontWeight: 500, mb: 2}}>
          Personal
        </Typography>
        <Stack spacing={0} sx={{mb: 4}}>
          {personalSections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <Paper
                key={section.path}
                variant="outlined"
                onClick={() => navigate(section.path)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  transition: 'all 0.15s',
                  '&:hover': {
                    borderColor: 'primary.light',
                    bgcolor: 'action.hover',
                  },
                  borderRadius: 0,
                  '&:first-of-type': {borderTopLeftRadius: 8, borderTopRightRadius: 8},
                  '&:last-of-type': {borderBottomLeftRadius: 8, borderBottomRightRadius: 8},
                  '&:not(:first-of-type)': {marginTop: '-1px'},
                }}
              >
                <Icon sx={{fontSize: 22, color: 'text.secondary'}} />
                <Box sx={{flex: 1, minWidth: 0}}>
                  <Typography variant="subtitle2" sx={{fontWeight: 500}}>
                    {section.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {section.description}
                  </Typography>
                </Box>
                <ChevronRightIcon sx={{fontSize: 20, color: 'text.disabled'}} />
              </Paper>
            );
          })}
        </Stack>

        {/* Organization Section */}
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2}}>
          <Typography variant="h6" sx={{fontWeight: 500}}>
            Organizations
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/org/new')}
          >
            New
          </Button>
        </Box>

        {organizations.length === 0 ? (
          <Paper variant="outlined" sx={{p: 4, textAlign: 'center'}}>
            <BusinessIcon sx={{fontSize: 48, color: 'text.disabled', mb: 1}} />
            <Typography variant="body1" color="text.secondary" sx={{mb: 2}}>
              No organizations yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
              Create an organization to publish guides under a unique URL.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/org/new')}
            >
              Create Organization
            </Button>
          </Paper>
        ) : (
          <Stack spacing={0}>
            {sortedOrgs.map(org => {
              const isActive = activeOrg?.id === org.id;
              return (
                <Paper
                  key={org.id}
                  variant="outlined"
                  onClick={() => !isActive && switchOrg(org.id)}
                  sx={{
                    p: 2,
                    cursor: isActive ? 'default' : 'pointer',
                    borderColor: isActive ? 'primary.main' : 'divider',
                    borderWidth: isActive ? 2 : 1,
                    bgcolor: isActive ? 'primary.50' : 'background.paper',
                    transition: 'all 0.15s',
                    '&:hover': isActive ? {} : {
                      borderColor: 'primary.light',
                      bgcolor: 'action.hover',
                    },
                    // Collapse borders between items
                    borderRadius: 0,
                    '&:first-of-type': {borderTopLeftRadius: 8, borderTopRightRadius: 8},
                    '&:last-of-type': {borderBottomLeftRadius: 8, borderBottomRightRadius: 8},
                    '&:not(:first-of-type)': {marginTop: '-1px'},
                  }}
                >
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                    {isActive ? (
                      <CheckCircleIcon color="primary" sx={{fontSize: 22}} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{fontSize: 22, color: 'text.disabled'}} />
                    )}
                    <Box sx={{flex: 1, minWidth: 0}}>
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: isActive ? 600 : 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {org.name}
                        </Typography>
                        {isActive && (
                          <Chip label="Active" size="small" color="primary" sx={{height: 20, fontSize: '0.7rem'}} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {org.slug} &middot; Created {formatDate(org.createdAt)}
                        {org.website && <> &middot; {org.website}</>}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
